import { useNavigation } from '@react-navigation/native';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Platform,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  Image,
} from 'react-native';

import { IconSizes } from '@components/shared/Icon/RestyleIcon';
import { File } from '@graphql/generated';
import useMe from '@hooks/useMe';
import {
  getExpiryTimestamp,
  shouldReplaceAvatarSource,
} from '@utils/urlExpiry';

import theme, { getScaledSize } from '../../../themes/theme';
import CachedImage from '../../components/CachedImage/CachedImage';
import useColors from '../../hooks/useColors';
import { Box, Text } from '../Restyle/index';
import Icon from '../shared/Icon/Icon';

export interface AvatarProps {
  id?: string;
  avatarType?: 'chat' | 'team' | 'user';
  /**
   * How large should the avatar be?
   */
  size?:
    | 'memberList'
    | 'xxs'
    | 'xs'
    | 'small'
    | 'medium'
    | 'default'
    | 'large'
    | 'large64';
  /**
   * What are the initials?
   */
  label?: string | null;
  /**
   * Text variant for the initials fallback; overrides the size-derived
   * variant (e.g. 'labelHeroCarousel' for the 24px hero-card avatars).
   */
  labelVariant?: Exclude<keyof typeof theme.textVariants, 'defaults'>;
  /**
   * What background color to use?
   */
  backgroundColor?: string;
  /**
   * What avatar File object to use?
   */
  avatar?: File | null;
  /**
   * What avatar URL string to use? (used when avatar File is not available)
   */
  avatarUrl?: string | null;
  /**
   * Whether there is a T subscript?
   */
  marginLeft?: number;
  onPress?: () => void;
  onDismiss?: () => void;
  isMultiple?: boolean;
  skipCache?: boolean;
  disableAvatarPress?: boolean;
  isSmall?: boolean;
  isSenderFriendRequest?: boolean;
  width?: number;
  height?: number;
  accessibilityLabel?: string;
  groupColorSeed?: string;
}

/**
 * User avatar component
 */
const Avatar = ({
  id,
  avatarType = 'user',
  size = 'default',
  label = null,
  labelVariant,
  backgroundColor = undefined,
  avatar = null,
  avatarUrl = null,
  marginLeft = 0,
  onPress,
  onDismiss,
  isMultiple,
  skipCache = true,
  disableAvatarPress,
  isSmall = false,
  isSenderFriendRequest = false,
  width,
  height,
  accessibilityLabel,
  groupColorSeed,
}: AvatarProps) => {
  const { me } = useMe();
  const navigation = useNavigation();
  const { getComputedColor, getGroupColor } = useColors();
  const defaultIcon = isMultiple ? 'Users' : 'User';
  const [avatarUrlFailed, setAvatarUrlFailed] = useState(false);

  const imageAccessibilityLabel = accessibilityLabel ?? 'Avatar image';

  // S3 presigned URLs rotate their query-string signature on every server
  // response. Memoising by the stable path (before `?`) prevents Image from
  // receiving a new source object — and therefore re-downloading the file —
  // when only the signing params change. (TA-10111)
  //
  // TA-10914: comparing by path alone also meant a genuinely expired
  // signature was never replaced — `stableAvatarSource` kept the stale `uri`
  // forever once memoised, leaving the avatar permanently grey after a 403.
  // Hold the last-accepted full URL across renders (store-info-from-previous-
  // render, during render — no effect needed) and only swap in a new
  // candidate when it points at a different object, or the held signature
  // has actually expired/is invalid. A same-object rotation while the held
  // signature is still valid keeps being ignored, preserving TA-10111.
  //
  // Re-run this check on every render — not just when `avatarUrl` changes
  // since the previous render — instead of gating it behind a "did the
  // candidate change" flag. `shouldReplaceAvatarSource` is already a no-op
  // when `avatarUrl` matches what's held, so this costs nothing once
  // settled; but it also means a *previously rejected* (still-valid at the
  // time) rotation isn't discarded — if `acceptedAvatarUrl` has since
  // expired and `avatarUrl` didn't change again, the next render (for any
  // reason) still promotes it, instead of being stuck comparing against a
  // stale "last seen" snapshot that itself always looked fresh enough.
  const [acceptedAvatarUrl, setAcceptedAvatarUrl] = useState(avatarUrl);
  if (shouldReplaceAvatarSource(acceptedAvatarUrl, avatarUrl, new Date())) {
    setAcceptedAvatarUrl(avatarUrl);
  }

  // Reset error state whenever the URL actually being displayed changes.
  // `acceptedAvatarUrl` is included (PR review, TA-10914) alongside
  // `avatarUrl`: the expiry timer below can promote `acceptedAvatarUrl`
  // without `avatarUrl` itself changing on that render (it already changed
  // on an earlier, rejected-while-valid render). Without this, a
  // currently-failed image that later gets a fresh URL via the timer would
  // stay stuck on the fallback icon forever — `avatarUrlFailed` would never
  // reset, so the newly-accepted URL never gets a render attempt at all.
  useEffect(() => {
    setAvatarUrlFailed(false);
  }, [avatarUrl, acceptedAvatarUrl]);

  // PR review (TA-10914): the promotion check above only re-runs when this
  // component actually renders. If a same-object rotation is rejected while
  // still valid and `avatarUrl` never changes again, nothing forces a later
  // render once the held signature actually expires — the outer
  // `React.memo` can keep bailing on unrelated parent re-renders forever,
  // leaving a stale/403 avatar displayed indefinitely. Schedule a promotion
  // check for exactly the held URL's real expiry moment instead of relying
  // on an incidental re-render to catch it.
  useEffect(() => {
    const expiresAt = acceptedAvatarUrl
      ? getExpiryTimestamp(acceptedAvatarUrl)
      : null;
    if (expiresAt == null) return undefined;

    // +1s margin: a timer that fires at the exact expiry millisecond would
    // have `isUrlExpired`'s strict `expirationDate < now` still read as
    // "not yet expired" on that boundary tick. Firing a moment after
    // guarantees the promotion check below actually sees it as expired.
    const timer = setTimeout(
      () => {
        setAcceptedAvatarUrl((prevAccepted) =>
          shouldReplaceAvatarSource(prevAccepted, avatarUrl, new Date())
            ? avatarUrl
            : prevAccepted
        );
      },
      Math.max(expiresAt - Date.now(), 0) + 1000
    );
    return () => clearTimeout(timer);
  }, [acceptedAvatarUrl, avatarUrl]);

  const stableAvatarSource = useMemo(
    () => (acceptedAvatarUrl ? { uri: acceptedAvatarUrl } : undefined),
    [acceptedAvatarUrl]
  );

  if (label) {
    label = label.toUpperCase();
    const matches = label.match(/\b(\w)/g) || [];
    label = matches.length > 1 ? matches.join('') : label;
    label = label.slice(0, 2);
  }

  if (!backgroundColor) {
    const colorSeed = isMultiple ? (groupColorSeed ?? id) : label;
    const color = isMultiple
      ? getGroupColor(colorSeed)
      : getComputedColor(label);
    backgroundColor = isSenderFriendRequest ? theme.colors.grey04 : color.value;
  }

  const onAvatarPress = () => {
    (navigation as any).navigate('user-profile', {
      userId: id,
    });
  };

  const sizeVariantMap: Record<
    string,
    'metadataSecondary' | 'metadata' | 'link' | 'buttonLabel' | 'heading1'
  > = {
    xxs: 'metadataSecondary',
    xs: 'metadata',
    small: 'link',
    medium: 'buttonLabel',
    large: 'heading1',
  };

  const renderItem = (style: AvatarStyleSheet) => {
    const boxSize =
      width && height
        ? { width, height, borderRadius: width / 2, zIndex: -1 }
        : style.avatarBox;

    const variant = labelVariant ?? (sizeVariantMap[size] || 'heading2');

    const sizePropsMap: Record<
      string,
      { variant: IconSizes; width?: number; height?: number }
    > = {
      xxs: { variant: 'xxs' },
      xs: { variant: 'xs' },
      small: { variant: 's' },
      medium: { variant: 'l' },
      large: { variant: 'l', width: 40, height: 40 },
    };

    const iconProps = sizePropsMap[size] || {
      variant: 'l',
      width: 32,
      height: 32,
    };

    const getLabelOrIcon = () => {
      return label && !isMultiple ? (
        <Box>
          <Text
            variant={variant}
            color={backgroundColor === theme.colors.grey02 ? 'grey04' : 'white'}
            style={[
              boxSize.width === 16 && { fontSize: 9 },
              boxSize.width === 20 && { fontSize: 10 },
            ]}>
            {label}
          </Text>
        </Box>
      ) : (
        <Box>
          <Icon
            name={defaultIcon}
            color={backgroundColor === theme.colors.grey02 ? 'grey04' : 'white'}
            {...iconProps}
          />
        </Box>
      );
    };

    const usingImage = !!avatar || (!!avatarUrl && !avatarUrlFailed);

    const getAvatarContent = () => {
      if (avatar) {
        return (
          <CachedImage
            avatarType={avatarType}
            avatarId={avatar.id}
            image={avatar}
            skipCache={skipCache}
            width={width ?? style.avatarBox.width}
            height={height ?? style.avatarBox.height}
            borderRadius={width ? width / 2 : style.avatarBox.borderRadius}
          />
        );
      }
      if (avatarUrl && !avatarUrlFailed) {
        return (
          <Image
            source={stableAvatarSource!}
            style={{
              width: width ?? style.avatarBox.width,
              height: height ?? style.avatarBox.height,
              borderRadius: width ? width / 2 : style.avatarBox.borderRadius,
            }}
            resizeMode='cover'
            accessible
            accessibilityLabel={imageAccessibilityLabel}
            onError={() => setAvatarUrlFailed(true)}
          />
        );
      }
      return getLabelOrIcon();
    };

    return (
      <TouchableOpacity
        onPress={onPress ?? onAvatarPress}
        activeOpacity={onPress || (id && id !== me?.id) ? 0.2 : 1}
        accessibilityLabel={accessibilityLabel}
        disabled={(!onPress && !id) || disableAvatarPress}>
        <Box
          style={[
            {
              backgroundColor: usingImage
                ? theme.colors.grey02
                : backgroundColor,
              marginLeft: marginLeft,
            },
            isSmall ? { width: 24, height: 24 } : boxSize,
          ]}
          alignItems='center'
          justifyContent='center'
          borderWidth={0}
          borderColor='white'>
          {getAvatarContent()}
        </Box>
        {!!onDismiss && (
          <Box
            style={{
              position: 'absolute',
              top: -theme.spacing.xxs,
              right: -theme.spacing.xs,
            }}>
            <Icon
              name='X2'
              onPress={onDismiss}
              color='black'
              variant='l'
              accessibilityLabel='Remove selected member'
            />
          </Box>
        )}
      </TouchableOpacity>
    );
  };

  const avatarBoxStyleForMemberListSummary = () => ({
    backgroundColor: avatar ? theme.colors.white : backgroundColor,
    marginLeft: marginLeft,
  });

  const renderItemForMemberListSummary = () => {
    const getLabelOrIcon = () => {
      return label ? (
        <Text variant='metadata' color='white'>
          {label}
        </Text>
      ) : (
        <Icon name={defaultIcon} color='white' variant='xs' />
      );
    };

    const getAvatarContentForMemberList = () => {
      if (avatar) {
        return (
          <CachedImage
            avatarType={avatarType}
            avatarId={avatar.id}
            image={avatar}
            width={isSmall ? 24 : stylesForMemberListSummary.avatar.width}
            height={isSmall ? 24 : stylesForMemberListSummary.avatar.height}
            borderRadius={
              isSmall ? 12 : stylesForMemberListSummary.avatar.borderRadius
            }
          />
        );
      }
      if (avatarUrl && !avatarUrlFailed) {
        return (
          <Image
            source={stableAvatarSource!}
            style={{
              width: isSmall ? 24 : stylesForMemberListSummary.avatar.width,
              height: isSmall ? 24 : stylesForMemberListSummary.avatar.height,
              borderRadius: isSmall
                ? 12
                : stylesForMemberListSummary.avatar.borderRadius,
            }}
            resizeMode='cover'
            accessible
            accessibilityLabel={imageAccessibilityLabel}
            onError={() => setAvatarUrlFailed(true)}
          />
        );
      }
      return getLabelOrIcon();
    };

    return (
      <Box
        style={[
          avatarBoxStyleForMemberListSummary(),
          isSmall
            ? { width: 24, height: 24 }
            : stylesForMemberListSummary.avatarBox,
        ]}
        borderRadius='s'
        borderColor='white'
        alignItems='center'
        justifyContent='center'
        borderWidth={1}>
        {getAvatarContentForMemberList()}
      </Box>
    );
  };

  const sizeRenderMap: Record<string, () => React.ReactElement> = {
    memberList: renderItemForMemberListSummary,
    xxs: () => renderItem(stylesXXS),
    xs: () => renderItem(stylesXS),
    small: () => renderItem(stylesSmall),
    medium: () => renderItem(stylesMedium),
    large: () => renderItem(stylesLarge),
    large64: () => renderItem(stylesLarge64),
  };

  const renderComponent =
    sizeRenderMap[size] || (() => renderItem(stylesDefault));

  return renderComponent();
};

type AvatarStyleSheet = {
  avatarBox: {
    width: number;
    height: number;
    borderRadius: number;
    zIndex?: number;
  };
  labelTBox: {
    width: number;
    height: number;
    right: number;
  };
};

const stylesDefault = StyleSheet.create<AvatarStyleSheet>({
  avatarBox: {
    width: getScaledSize(48),
    height: getScaledSize(48),
    borderRadius: getScaledSize(24),
    zIndex: -1,
  },
  labelTBox: {
    width: getScaledSize(12),
    height: getScaledSize(12),
    right: 0.8,
  },
});

const stylesXS = StyleSheet.create<AvatarStyleSheet>({
  avatarBox: {
    width: getScaledSize(24),
    height: getScaledSize(24),
    borderRadius: getScaledSize(16),
  },
  labelTBox: {
    width: getScaledSize(10),
    height: getScaledSize(10),
    right: 0,
  },
});

const stylesXXS = StyleSheet.create<AvatarStyleSheet>({
  avatarBox: {
    width: getScaledSize(20),
    height: getScaledSize(20),
    borderRadius: getScaledSize(10),
  },
  labelTBox: {
    width: getScaledSize(10),
    height: getScaledSize(10),
    right: 0,
  },
});

const stylesSmall = StyleSheet.create<AvatarStyleSheet>({
  avatarBox: {
    width: getScaledSize(32),
    height: getScaledSize(32),
    borderRadius: getScaledSize(16),
  },
  labelTBox: {
    width: getScaledSize(10),
    height: getScaledSize(10),
    right: 0,
  },
});

const stylesMedium = StyleSheet.create<AvatarStyleSheet>({
  avatarBox: {
    width: getScaledSize(40),
    height: getScaledSize(40),
    borderRadius: getScaledSize(20),
  },
  labelTBox: {
    width: getScaledSize(10),
    height: getScaledSize(10),
    right: 1.9,
  },
});

const stylesLarge = StyleSheet.create<AvatarStyleSheet>({
  avatarBox: {
    width: getScaledSize(56),
    height: getScaledSize(56),
    borderRadius: getScaledSize(28),
  },
  labelTBox: {
    width: getScaledSize(12),
    height: getScaledSize(12),
    right: 1.9,
  },
});

const stylesLarge64 = StyleSheet.create<AvatarStyleSheet>({
  avatarBox: {
    width: getScaledSize(64),
    height: getScaledSize(64),
    borderRadius: getScaledSize(32),
  },
  labelTBox: {
    width: getScaledSize(14),
    height: getScaledSize(14),
    right: 2.2,
  },
});

const stylesForMemberListSummary = StyleSheet.create({
  avatarBox:
    Platform.OS === 'web'
      ? {
          width: Dimensions.get('window').width < 1052 ? 26 : 32,
          height: Dimensions.get('window').width < 1052 ? 26 : 32,
          borderRadius: Dimensions.get('window').width < 1052 ? 13 : 16,
        }
      : { width: 24, height: 24 },
  avatar:
    Platform.OS === 'web'
      ? {
          width: 30,
          height: 30,
          borderRadius: 15,
        }
      : {
          width: 22,
          height: 22,
          borderRadius: 11,
        },
});

const avatarPropsAreEqual = (prev: AvatarProps, next: AvatarProps): boolean => {
  if (prev.avatar?.id !== next.avatar?.id) return false;
  if (prev.id !== next.id) return false;
  if (prev.avatarType !== next.avatarType) return false;
  if (prev.size !== next.size) return false;
  if (prev.label !== next.label) return false;
  if (prev.labelVariant !== next.labelVariant) return false;
  if (prev.backgroundColor !== next.backgroundColor) return false;
  // TA-10914: exact-string comparison, not path-only. A path-only comparator
  // here previously bailed out of re-rendering based on whether the LAST
  // PROP (not what's actually displayed) looked expired — once one valid
  // rotation was skipped, every later comparison kept checking an
  // increasingly-fresh "prev" against a fresh "next" and could never detect
  // that the truly-displayed, older URL had gone stale, permanently
  // swallowing the update. Comparing exact strings has no such drift: it's
  // always correct about whether the prop actually changed. Suppressing the
  // *visible* reload for a still-valid same-object rotation is handled
  // downstream by `acceptedAvatarUrl`/`shouldReplaceAvatarSource` instead,
  // so this component re-running for a cosmetic signature change is cheap
  // and harmless — `stableAvatarSource`'s memo still won't produce a new
  // object when the accepted URL hasn't actually changed.
  if (prev.avatarUrl !== next.avatarUrl) return false;
  if (prev.marginLeft !== next.marginLeft) return false;
  if (prev.onPress !== next.onPress) return false;
  if (prev.onDismiss !== next.onDismiss) return false;
  if (prev.isMultiple !== next.isMultiple) return false;
  if (prev.skipCache !== next.skipCache) return false;
  if (prev.disableAvatarPress !== next.disableAvatarPress) return false;
  if (prev.isSmall !== next.isSmall) return false;
  if (prev.isSenderFriendRequest !== next.isSenderFriendRequest) return false;
  if (prev.width !== next.width) return false;
  if (prev.height !== next.height) return false;
  if (prev.accessibilityLabel !== next.accessibilityLabel) return false;
  if (prev.groupColorSeed !== next.groupColorSeed) return false;
  return true;
};

export default React.memo(Avatar, avatarPropsAreEqual);
